import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLayoutEffect, useRef } from 'react';
import { apiErrorText } from '@/api/opengater/errors';
import { EditorToolbar } from './EditorToolbar';
import { dragHasFiles, filesOf, imageFileProblem } from './imageFiles';
import { applyMarkdownAction, editOnEnter, insertBlock, settlePlaceholder, uploadPlaceholder, } from './markdownEdit';
import s from './MarkdownEditor.module.css';
/** ⌘/Ctrl + key; by the key's place, so a Russian layout works too. */
const HOTKEYS = { KeyB: 'bold', KeyI: 'italic', KeyK: 'link' };
/** The article as Markdown source: a plain field with helpers for the syntax. */
export function SourceEditor({ value, onChange, disabled, expanded, upload, notify, controls, }) {
    const textareaRef = useRef(null);
    const fileRef = useRef(null);
    // The latest text for uploads that finish later; edits made meanwhile stay.
    const valueRef = useRef(value);
    const uploads = useRef(0);
    const pendingSelection = useRef(null);
    useLayoutEffect(() => {
        valueRef.current = value;
    }, [value]);
    // The field grows with the text — and refits when the panel's width changes
    // the wrapping; expanded, it fills the window and scrolls.
    useLayoutEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const fit = () => {
            textarea.style.height = '';
            if (expanded || !textarea.scrollHeight)
                return;
            textarea.style.height = `${textarea.scrollHeight}px`;
        };
        fit();
        if (typeof ResizeObserver === 'undefined')
            return;
        let width = textarea.clientWidth;
        const observer = new ResizeObserver(() => {
            if (textarea.clientWidth === width)
                return;
            width = textarea.clientWidth;
            fit();
        });
        observer.observe(textarea);
        return () => observer.disconnect();
    }, [value, expanded]);
    useLayoutEffect(() => {
        const selection = pendingSelection.current;
        const textarea = textareaRef.current;
        if (!selection || !textarea)
            return;
        pendingSelection.current = null;
        if (selection.focus)
            textarea.focus();
        else if (document.activeElement !== textarea)
            return;
        textarea.setSelectionRange(selection.start, selection.end);
    }, [value, expanded]);
    const commitEdit = (result) => {
        pendingSelection.current = {
            start: result.selectionStart,
            end: result.selectionEnd,
            focus: true,
        };
        valueRef.current = result.value;
        onChange(result.value);
    };
    const format = (action) => {
        const textarea = textareaRef.current;
        if (!textarea || disabled)
            return;
        commitEdit(applyMarkdownAction(value, textarea.selectionStart, textarea.selectionEnd, action));
    };
    /** Puts the finished upload in place of its stand-in, keeping the caret where it was. */
    const settle = (placeholder, image) => {
        const before = valueRef.current;
        const at = before.indexOf(placeholder);
        if (at === -1)
            return;
        const next = settlePlaceholder(before, placeholder, image);
        const textarea = textareaRef.current;
        if (textarea && document.activeElement === textarea) {
            const shift = next.length - before.length;
            const move = (pos) => (pos > at ? Math.max(at, pos + shift) : pos);
            pendingSelection.current = {
                start: move(textarea.selectionStart),
                end: move(textarea.selectionEnd),
                focus: false,
            };
        }
        valueRef.current = next;
        onChange(next);
    };
    const uploadImages = (files) => {
        if (!upload || disabled || files.length === 0)
            return;
        const problems = files.map(imageFileProblem).filter((p) => p !== null);
        const images = files.filter((file) => imageFileProblem(file) === null);
        notify(problems.length ? problems.join('; ') : null);
        if (images.length === 0)
            return;
        const textarea = textareaRef.current;
        const end = valueRef.current.length;
        let edit = {
            value: valueRef.current,
            selectionStart: textarea?.selectionStart ?? end,
            selectionEnd: textarea?.selectionEnd ?? end,
        };
        const jobs = images.map((file) => {
            uploads.current += 1;
            const placeholder = uploadPlaceholder(String(uploads.current), file.name);
            edit = insertBlock(edit.value, edit.selectionStart, edit.selectionEnd, placeholder);
            return { file, placeholder };
        });
        commitEdit(edit);
        for (const { file, placeholder } of jobs) {
            upload(file).then((url) => settle(placeholder, `![](${url})`), (error) => {
                settle(placeholder, null);
                const reason = apiErrorText(error, error instanceof Error ? error.message : 'ошибка');
                notify(`Не удалось загрузить ${file.name}: ${reason}`);
            });
        }
    };
    const onKeyDown = (event) => {
        if (event.nativeEvent.isComposing)
            return;
        const mod = event.metaKey || event.ctrlKey;
        if (mod) {
            const action = !event.altKey && !event.shiftKey ? HOTKEYS[event.code] : undefined;
            if (action) {
                event.preventDefault();
                format(action);
            }
            return;
        }
        if (event.key !== 'Enter' || event.altKey)
            return;
        const { selectionStart: start, selectionEnd: end } = event.currentTarget;
        if (event.shiftKey) {
            event.preventDefault();
            commitEdit({
                value: `${value.slice(0, start)}  \n${value.slice(end)}`,
                selectionStart: start + 3,
                selectionEnd: start + 3,
            });
            return;
        }
        const edit = editOnEnter(value, start, end);
        if (!edit)
            return;
        event.preventDefault();
        commitEdit(edit);
    };
    const onPaste = (event) => {
        const data = event.clipboardData;
        const files = filesOf(data?.files);
        // Copied text may carry a picture along (a document, a web page): paste the text.
        if (!upload || files.length === 0 || data.getData('text/plain'))
            return;
        event.preventDefault();
        uploadImages(files);
    };
    const onTool = (id) => {
        if (id === 'image')
            fileRef.current?.click();
        else
            format(id);
    };
    return (_jsxs("div", { className: s.mode, onDrop: (event) => {
            if (!upload || disabled || !dragHasFiles(event.dataTransfer?.types))
                return;
            event.preventDefault();
            uploadImages(filesOf(event.dataTransfer.files));
        }, children: [_jsx(EditorToolbar, { disabled: disabled, images: !!upload, onTool: onTool, controls: controls }), _jsx("textarea", { ref: textareaRef, className: s.textarea, "aria-label": "\u0422\u0435\u043A\u0441\u0442 \u0441\u0442\u0430\u0442\u044C\u0438 \u0432 Markdown", value: value, disabled: disabled, spellCheck: "true", placeholder: "\u041D\u0430\u0447\u043D\u0438\u0442\u0435 \u043F\u0438\u0441\u0430\u0442\u044C \u0441\u0442\u0430\u0442\u044C\u044E\u2026", onChange: (event) => onChange(event.target.value), onKeyDown: onKeyDown, onPaste: onPaste }), upload && (_jsx("input", { ref: fileRef, type: "file", accept: "image/*", multiple: true, hidden: true, disabled: disabled, onChange: (event) => {
                    uploadImages(filesOf(event.target.files));
                    event.target.value = '';
                } }))] }));
}
